import axios from 'axios';
import { app_version } from './Utils';
import { ProductionEntry, StockItem, EmployeeAllocation } from './Models';

export interface CopyEmployeeAllocationRequest {
    sourceDate: string;
    sourceSessionId: number;
    sourceLineId: number;
    destinationDate: string;
    destinationSessionId: number;
    destinationLineId: number;
}

export const serverUrl = `${process.env.REACT_APP_SERVER_URL+ '/api' || ''}`;

axios.defaults.headers.post['Content-Type'] ='application/json';

const resolveMock = async <T,>(value: T): Promise<T> => {
    return new Promise((resolve) => {
        setTimeout(() => resolve(value), 150);
    });
};

const getToday = () => new Date().toISOString().split('T')[0];

const ArthaGateway = {
    validateLogin: async function(username, password) {
        try {
            const payload = { username: username, password: password };
            let response = await axios.post(serverUrl + '/user/validate', payload);
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::validateLogin ->"+ error);
            throw error;
        }
        return false;
    },
    getTrips: async function(durationId: string, status: number, pageNumber: number = 1, pageSize: number = 1000) {
        try {
            const params = { durationId, status, pageNumber, pageSize  };
            let response = await axios.get(serverUrl + '/voucher/trip', { params });
            if (response) {            
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::getTrips ->"+ error);
            throw error;
        }
        return undefined;
    },
    getTrip: async function(id: number) {
        try {
            let response = await axios.get(serverUrl + '/voucher/trip/' + id);
            if (response) {            
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::getTrip ->"+ error);
            throw error;
        }
        return undefined;
    },    
    getTripByCode: async function(code: string) {
        try {
            //let response = await axios.get(serverUrl + '/voucher/trip?code=' + code);
            let response = await axios.get(serverUrl + '/voucher/trip/' + code);
            if (response) {            
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::getTrip ->"+ error);
            throw error;
        }
        return undefined;
    },
    markTripAsLoaded: async function(tripId: number) {
        try {
            let response = await axios.get(serverUrl + '/voucher/trip/load?tripId='+ tripId);
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::completePacking ->"+ error);
            throw error;
        }
        return false;
    },
    unblockTrip: async function(tripId: number, adminPIN: string) {
        try {
            const params = { id: tripId, adminPIN };
            let response = await axios.get(serverUrl + '/voucher/trip/unblock', { params });
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::unblockTrip ->"+ error);
            throw error;
        }
        return false;
    },
    unloadOrder: async function(orderId: number, tripId: number) {
        try {
            let response = await axios.get(serverUrl + '/voucher/trip/unload?tripId='+ tripId + '&orderId='+ orderId);
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::completePacking ->"+ error);
            throw error;
        }
        return false;
    },
    markPackageAsLoaded: async function(tripId: number, packageId: number) {
        try {
            const params = { tripId, packageId };
            let response = await axios.get(serverUrl + '/voucher/package/loaded',{ params });
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::completePacking ->"+ error);
            throw error;
        }
        return false;
    },
    checkIfPackageDestroyed: async function(tripId: number, packageNumber: string) {
        try {
            const params = { tripId, packageNumber };
            let response = await axios.get(serverUrl + '/voucher/package/checkIfDestroyed',{ params });
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::completePacking ->"+ error);
            throw error;
        }
        return false;
    },
    updateVehicleDetails: async function(vehicleData: any) {
        try {
            let response = await axios.post(serverUrl + '/voucher/trip/updateVehicle', vehicleData);
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::updateVehicleDetails ->"+ error);
            throw error;
        }
        return false;
    },
    getEmployees: async function(name, groupId, pageNumber = 1, pageSize = 100, fields = 'Id,Name,EmployeeNumber,GroupName,PseudoName') {
        try {
            const params = { name, groupId, pageNumber, pageSize, activeOnly: true, fields };
            let response = await axios.get(serverUrl + '/master/employee', { params });
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::getEmployees ->"+ error);
            throw error;
        }
        return undefined;
    },    
    postGatepass: async function(gatepass) {
        try {
            let response = await axios.post(serverUrl + '/attendance/gatepass', gatepass);
            if (response) {            
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::postGatepass ->"+ error);
            throw error;
        }
        return undefined;
    },
    getGatepass: async function(employeeId, durationId) {
        try {
            const params = { employeeId, durationId  };
            let response = await axios.get(serverUrl + '/attendance/gatepass', { params });
            if (response) {            
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::getGatepass ->"+ error);
            throw error;
        }
        return undefined;
    },
    getStockStatus: async function(asOn: string = getToday(), varientName: string = '', packageName: string = ''): Promise<StockItem[]> {
        try {
            const params = {
                type: 0,
                brandId: 0,
                groupId: 0,
                godownId: 0,
                name: '',
                asOn,
                varientName,
                packageName,
            };
            let response = await axios.get(serverUrl + '/stock/itemwise', { params });
            if (response) {
                return response.data || [];
            }
        } catch (error) {
            console.error('Error in ArthaGateway::getStockStatus ->' + error);
            throw error;
        }
        return [];
    },
    getProductLines: async function(plantId: number = 0) {
        try {
            const params = { plantId: plantId };
            let response = await axios.get(serverUrl + '/production/line', { params });
            if (response) {            
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::getProductLines ->"+ error);
            throw error;
        }
        return [];
    },
    getProductionPlants: async function() {
        try {
            let response = await axios.get(serverUrl + '/production/plant');
            if (response) {            
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::getProductionPlants ->"+ error);
            throw error;
        }
        return [];
    },
    getWorkSessions: async function() {
        try {
            let response = await axios.get(serverUrl + '/production/session');
            if (response) {            
                return response.data;
            }
        } catch (error) {
            console.error("Error in ArthaGateway::getWorkSessions ->"+ error);
            throw error;
        }
        return [];
    },
    getEmployeeAllocations: async function(date: string, sessionId: number, plantId?: number, lineId?: number): Promise<EmployeeAllocation | null> {
        try {
            const params = { from: date, to: date, sessionId, plantId, lineId: lineId || 0 };
            let response = await axios.get(serverUrl + '/production/employeeAllocation', { params });
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error('Error in ArthaGateway::getEmployeeAllocations ->' + error);
            throw error;
        }
        return null;
    },
    assignEmployeeAllocation: async function(allocation: EmployeeAllocation): Promise<EmployeeAllocation | null> {
        try {
            let response = await axios.post(serverUrl + '/production/employeeAllocation/assign', allocation);
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error('Error in ArthaGateway::assignEmployeeAllocation ->' + error);
            throw error;
        }
        return null;
    },
    deassignEmployeeAllocation: async function(date: string, sessionId: number, lineId: number, employeeId: number): Promise<EmployeeAllocation | null> {
        try {
            let params = { date, sessionId, lineId, employeeId };
            let response = await axios.delete(serverUrl + '/production/employeeAllocation/deassign', { params });
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error('Error in ArthaGateway::deassignEmployeeAllocation ->' + error);
            throw error;
        }
        return null;
    },
    copyEmployeeAllocation: async function(payload: CopyEmployeeAllocationRequest): Promise<EmployeeAllocation | null> {
        try {
            let response = await axios.post(serverUrl + '/production/employeeAllocation/copy', payload);
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error('Error in ArthaGateway::copyEmployeeAllocation ->' + error);
            throw error;
        }
        return null;
    },    
    getProductionEntries: async function(date: string, sessionId: number, lineId: number) {
        try {
            const params = { durationId: "-1", from: date, to: date, sessionId, lineId };
            let response = await axios.get(serverUrl + '/production/entry', { params });
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error('Error in ArthaGateway::getProductionEntries ->' + error);
            throw error;
        }
        return [];
    },
    saveProductionEntry: async function(entry: ProductionEntry): Promise<any | null> {
        try {
            let response = await axios.post(serverUrl + '/production/entry', entry);
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error('Error in ArthaGateway::saveProductionEntry ->' + error);
            throw error;
        }
        return null;
    },
    deleteProductionEntry: async function(date:string, sessionId:number, lineId: number, productId: number): Promise<any | null> {
        try {
            let params = { date, sessionId, lineId, productId };
            let response = await axios.delete(serverUrl + '/production/entry', { params });
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error('Error in ArthaGateway::deleteProductionEntry ->' + error);
            throw error;
        }
        return null;
    },
    getProducts: async function() {
        try {
            let param = { fields: "Id,Name,GroupName,UnitOfMeasureCode,VariantName1,VariantValue1,Weight,ItemsPerPackage", activeOnly: true };
            let response = await axios.get(serverUrl + '/master/product', { params: param });
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error('Error in ArthaGateway::getProducts ->' + error);
            throw error;
        }
    },
    getProductVariants: async function() {
        try {
            let response = await axios.get(serverUrl + '/master/varientOptions');
            if (response) {
                return response.data;
            }
        } catch (error) {
            console.error('Error in ArthaGateway::getProductVariants ->' + error);
            throw error;
        }
    }
};

export default ArthaGateway;